import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
// @ts-ignore
import { diffChars } from "diff";
import "./EditorPanel.css";
import axios from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import {
  X,
  FileText,
  Image,
  MessageSquare,
  Settings,
  Download,
  Unlock,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Trash2,
  Sparkles,
  PenLine,
  GitCompare,
  RotateCcw,
  Plus,
  Star,
  Send,
} from "lucide-react";
import {
  getChangedAnswers,
  getChangedPhotos,
  getChangedQuestions,
  applyDraftToPhotos,
  applyQuestionChanges,
} from "./draftUtils";
import { useAuth } from "../../context/AuthContext";
import client from "../../api/client";
import { COMABOOKS_COVERS, comabooksCoverById } from "./comabooksCovers";
import { COMABOOKS_BOOK_DESIGN, CbPhotoPreview, CbPhotoFormatPicker } from "./comabooksPhotos";
import {
  Answers,
  editorIdToNameMap,
  EditProps,
  Photo,
  QuestionAnswerPair,
  User,
  VersionActions,
  Versions,
} from "../EditPanel/types";
import saveAs from "file-saver";
import {
  questionTemplateToRecipientMap,
  DEFAULT_PROMPT,
} from "./constants";
import { apiCall, apiUpload } from "../../utils/apiProxy";
import {
  removeEmojis,
  convertTimestampToDate,
  getChapter,
  formatDate,
  formatDate2,
  formatActionDate,
} from "./helpers";
import ApprovalPopup from "../ApprovalPopup";
import SendToClientPopup, { sentToast } from "../SendToClientPopup";
import ProofComments, { ProofWithComments } from "./components/ProofComments";

// Photo-frame ассеты — те же 5 PNG, что фронт использует в PhotoPage и
// бэк в pdfGenerator (frames/), скопированы 1-в-1 для админ-превью.
import frameSquare from "../../assets/frames/square.png";
import frameLandscape from "../../assets/frames/landscape.png";
import frameLandscapeWide from "../../assets/frames/landscape-wide.png";
import framePortrait from "../../assets/frames/portrait.png";
import framePortraitTall from "../../assets/frames/portrait-tall.png";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type ViewMode = "basic" | "answers" | "photos";

// Auto-resize textarea component
interface AutoResizeTextareaProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const AutoResizeTextareaInner: React.FC<AutoResizeTextareaProps> = ({ value, onChange, className }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Локальный стейт держит то, что фактически в textarea. Это разрывает
  // цепочку «keystroke → setCorrectedAnswers → re-render всего
  // EditorPanel → 200 textarea'ой». Парент апдейтится только после
  // 200мс паузы — diff и save увидят свежее значение, ввод остаётся
  // плавным.
  const [local, setLocal] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Запоминаем последнее значение, которое мы сами пропихнули в парент.
  // Если приходит value === lastPropagated, это эхо нашей же отправки —
  // игнорируем, чтобы не сбросить пользовательский ввод. Если value
  // отличается — значит снаружи (например, переключение версии) и
  // надо синхронизировать local.
  const lastPropagatedRef = useRef(value);

  useEffect(() => {
    if (value !== lastPropagatedRef.current) {
      setLocal(value);
      lastPropagatedRef.current = value;
    }
  }, [value]);

  // Высоту под контент можно измерить только сбросив её в auto. Но у
  // длинного ответа это схлопывает поле с 600px до двух строк: страница
  // резко укорачивается, скроллящийся контейнер обрезает свой scrollTop по
  // новой высоте, а когда высота возвращается — прокрутка остаётся
  // обрезанной. Снаружи это выглядело так, что на каждом нажатии клавиши
  // вид отбрасывает к началу текстфилда, и тем сильнее, чем длиннее ответ.
  //
  // Поэтому запоминаем позиции прокрутки у всех скроллящихся родителей и у
  // окна, и возвращаем их сразу после замера. Работает это только в
  // useLayoutEffect (ниже): он выполняется до отрисовки, поэтому схлопывания
  // никто не увидит. В useEffect кадр уже отрисован — и скачок заметен.
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const scrollers: { el: HTMLElement; top: number }[] = [];
    for (let p = textarea.parentElement; p; p = p.parentElement) {
      if (p.scrollHeight > p.clientHeight) scrollers.push({ el: p, top: p.scrollTop });
    }
    const windowTop = window.scrollY;

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;

    for (const s of scrollers) s.el.scrollTop = s.top;
    if (window.scrollY !== windowTop) window.scrollTo(window.scrollX, windowTop);
  }, []);

  useLayoutEffect(() => {
    adjustHeight();
  }, [local, adjustHeight]);

  // Чистим висящий debounce при unmount, чтобы не дёрнуть onChange
  // мёртвого парента (особенно когда юзер быстро закрывает попап).
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocal(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      lastPropagatedRef.current = newValue;
      onChangeRef.current(newValue);
    }, 200);
  };

  return (
    <textarea
      ref={textareaRef}
      className={className}
      value={local}
      onChange={handleChange}
      style={{ overflow: 'hidden' }}
    />
  );
};

// React.memo с кастомным сравнением: игнорируем onChange (он берётся
// через ref внутри), сравниваем только value и className. Без этого
// одно нажатие клавиши вынуждало все 150–200 textarea'ой ре-рендериться.
const AutoResizeTextarea = React.memo(
  AutoResizeTextareaInner,
  (prev, next) => prev.value === next.value && prev.className === next.className,
);

// Diff View Component - shows differences between original and current text
interface DiffViewProps {
  originalText: string;
  currentText: string;
  onChange: (value: string) => void;
  className?: string;
}

// Get the caret's character offset within an element
const getCaretCharacterOffsetWithin = (element: HTMLElement): number => {
  let caretOffset = 0;
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    caretOffset = preCaretRange.toString().length;
  }
  return caretOffset;
};

// Set the caret position in the element given a character offset
const setCaretPosition = (element: HTMLElement, offset: number) => {
  let currentOffset = 0;
  const treeWalker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);
  let node: Node | null;
  while ((node = treeWalker.nextNode())) {
    const textLength = node.textContent?.length || 0;
    if (currentOffset + textLength >= offset) {
      const range = document.createRange();
      const newOffset = offset - currentOffset;
      range.setStart(node, newOffset);
      range.collapse(true);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      return;
    }
    currentOffset += textLength;
  }
};

const DiffView: React.FC<DiffViewProps> = ({ originalText, currentText, onChange, className }) => {
  const [html, setHtml] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);
  const caretOffsetRef = useRef<number>(0);

  // Recompute the diffed HTML when texts change (using character-based diff)
  useEffect(() => {
    const diff = diffChars(originalText || '', currentText || '');
    const highlightedHtml = diff
      .map((part: { value: string; added?: boolean; removed?: boolean }) => {
        if (part.added) {
          return `<span class="ep-diff-added">${part.value}</span>`;
        } else if (!part.removed) {
          return part.value;
        }
        return '';
      })
      .join('');
    setHtml(highlightedHtml);
  }, [originalText, currentText]);

  // After HTML is updated, restore the caret
  useEffect(() => {
    if (contentRef.current) {
      setTimeout(() => {
        setCaretPosition(contentRef.current!, caretOffsetRef.current);
      }, 0);
    }
  }, [html]);

  return (
    <div
      ref={contentRef}
      className={`${className} ep-diff-view`}
      contentEditable
      dir="ltr"
      suppressContentEditableWarning
      onKeyDown={(e) => {
        // Disable default behavior for Enter if Shift isn't pressed
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
        }
      }}
      onInput={(e) => {
        if (contentRef.current) {
          caretOffsetRef.current = getCaretCharacterOffsetWithin(contentRef.current);
        }
        onChange(e.currentTarget.innerText);
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Photo Preview Component — повторяет PhotoPage с фронта (тот же
// набор пресетов, те же frame-ассеты и метаданные слотов, та же
// механика placement текстов). 1:1 с PDF (см. pdfGenerator.ts) и с
// фронт-компонентом frontend/src/components/PhotoPage/PhotoPage.tsx.
interface PhotoPreviewProps {
  photo: Photo;
}

const PHOTO_FRAME_META: Record<
  string,
  { src: string; frameW: number; frameH: number; slotW: number; slotH: number; widthRatio: number }
> = {
  square:           { src: frameSquare,        frameW: 1103, frameH: 1086, slotW: 754, slotH: 759,  widthRatio: 0.78 },
  landscape:        { src: frameLandscape,     frameW: 1176, frameH: 805,  slotW: 903, slotH: 581,  widthRatio: 0.80 },
  "landscape-wide": { src: frameLandscapeWide, frameW: 1174, frameH: 691,  slotW: 975, slotH: 481,  widthRatio: 0.82 },
  portrait:         { src: framePortrait,      frameW: 1193, frameH: 1690, slotW: 811, slotH: 1261, widthRatio: 0.65 },
  "portrait-tall":  { src: framePortraitTall,  frameW: 805,  frameH: 1347, slotW: 544, slotH: 1098, widthRatio: 0.56 },
};

const PhotoPreview: React.FC<PhotoPreviewProps> = ({ photo }) => {
  if (COMABOOKS_BOOK_DESIGN) {
    return (
      <CbPhotoPreview
        status={photo.status}
        photoUrl={photo.photoUrl}
        date={formatDate(photo.date)}
        description={photo.description || ""}
        hideDate={photo.hideDate}
        hideDescription={photo.hideDescription}
      />
    );
  }
  // Парсер тот же что для редактирования preset'а (см. PHOTO_PRESET_KEYS).
  const raw = photo.status || "";
  const parts = raw.split("::");
  const preset = (Object.keys(PHOTO_FRAME_META).includes(parts[0]) || parts[0] === "fullbleed")
    ? parts[0]
    : "fullbleed";
  const [datePos = "top", dateSize = "sm"] = (parts[1] ?? "").split(":");
  const [descPos = "bottom", descSize = "md"] = (parts[2] ?? "").split(":");
  const color = parts[3] === "dark" ? "dark" : "light";

  const isFullbleed = preset === "fullbleed";
  const frame = isFullbleed ? null : PHOTO_FRAME_META[preset];

  const date = !photo.hideDate ? formatDate(photo.date) : "";
  const desc = !photo.hideDescription ? (photo.description || "") : "";

  type Slot = "top" | "center" | "bottom";
  const slots: Record<Slot, { kind: "date" | "desc"; text: string; size: string }[]> = {
    top: [], center: [], bottom: [],
  };
  if (date && datePos !== "hidden" && (datePos === "top" || datePos === "center" || datePos === "bottom")) {
    slots[datePos as Slot].push({ kind: "date", text: date, size: dateSize });
  }
  if (desc && descPos !== "hidden" && (descPos === "top" || descPos === "center" || descPos === "bottom")) {
    slots[descPos as Slot].push({ kind: "desc", text: desc, size: descSize });
  }

  return (
    <div className={`ep-pp ep-pp--${preset} ep-pp--color-${color}`}>
      {isFullbleed ? (
        <div className="ep-pp__photo-wrap">
          {photo.photoUrl
            ? <img className="ep-pp__photo" src={photo.photoUrl} alt="" />
            : <div className="ep-pp__photo-empty">нет фото</div>}
        </div>
      ) : (
        <div
          className="ep-pp__frame-wrap"
          style={{ width: `${frame!.widthRatio * 100}%`, aspectRatio: `${frame!.frameW} / ${frame!.frameH}` }}
        >
          <img className="ep-pp__frame-bg" src={frame!.src} alt="" />
          {photo.photoUrl ? (
            <img
              className="ep-pp__frame-photo"
              src={photo.photoUrl}
              alt=""
              style={{
                width: `${(frame!.slotW / frame!.frameW) * 100}%`,
                height: `${(frame!.slotH / frame!.frameH) * 100}%`,
              }}
            />
          ) : (
            <div
              className="ep-pp__frame-photo ep-pp__frame-empty"
              style={{
                width: `${(frame!.slotW / frame!.frameW) * 100}%`,
                height: `${(frame!.slotH / frame!.frameH) * 100}%`,
              }}
            >
              нет фото
            </div>
          )}
        </div>
      )}

      {(["top", "center", "bottom"] as Slot[]).map((slot) => {
        const entries = slots[slot];
        if (!entries.length) return null;
        const overlay = isFullbleed || slot === "center";
        return (
          <div
            key={slot}
            className={`ep-pp__slot ep-pp__slot--${slot} ${overlay ? "ep-pp__slot--overlay" : "ep-pp__slot--paper"}`}
          >
            {entries.map((e) => (
              <div
                key={e.kind}
                className={`ep-pp__text ep-pp__text--${e.kind} ep-pp__text--${e.size}`}
                style={{ whiteSpace: "pre-line" }}
              >
                {e.text}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// Опции расположения/размера/цвета текста на фото — зеркалят клиентский
// фронтенд (frontend/src/components/PhotoPage/photoPresets.ts): слоты, размеры
// и цвет хранятся в том же поле photo.status (формат
// <preset>::<datePos>:<dateSize>::<descPos>:<descSize>::<color>).
const PHOTO_SLOT_OPTS = [
  { key: "top", label: "сверху" },
  { key: "center", label: "по центру" },
  { key: "bottom", label: "снизу" },
] as const;
const PHOTO_SIZE_OPTS = [
  { key: "sm", label: "S" },
  { key: "md", label: "M" },
  { key: "lg", label: "L" },
] as const;
const PHOTO_COLOR_OPTS = [
  { key: "light", label: "Светлый" },
  { key: "dark", label: "Тёмный" },
] as const;

// Пресеты расположения названия на обложке. Значения совпадают с
// covers.plate_preset и с CoverPlatePreset в backend/src/services/
// coverGenerator.ts; ?preset= в запросе генерации перебивает значение из БД.
type CoverPreset = "frame" | "frame-top" | "left" | "center";

const COVER_PRESETS: { value: CoverPreset; label: string; hint: string }[] = [
  { value: "frame", label: "Frame", hint: "Декоративная плитка с заголовком по центру обложки" },
  { value: "frame-top", label: "Frame Top", hint: "Декоративная плитка с заголовком, прижата к верху" },
  { value: "left", label: "Left", hint: "Заголовок слева сверху, автор внизу" },
  { value: "center", label: "Center", hint: "Заголовок центром сверху, автор внизу" },
];

/** Пресет, выбранный юзером. У старых обложек поля нет — там дефолт 'frame',
 *  ровно как в колонке covers.plate_preset. */
const asCoverPreset = (v: unknown): CoverPreset =>
  COVER_PRESETS.some((p) => p.value === v) ? (v as CoverPreset) : "frame";

const EditorPanel: React.FC<EditProps> = ({ chosen, onClose }) => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [originalAnswers, setOriginalAnswers] = useState<Answers>({});
  const [correctedAnswers, setCorrectedAnswers] = useState<Answers>({});
  const [baseAnswers, setBaseAnswers] = useState<Answers>({}); // answers to compare against (original or version)
  const [originalPhotos, setOriginalPhotos] = useState<Photo[]>([]);
  const [originalQuestionPairs, setOriginalQuestionPairs] = useState<QuestionAnswerPair[]>([]);

  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);

  const [versions, setVersions] = useState<Versions[]>([]);
  const [currentVersion, setCurrentVersion] = useState<string | number | null>(null);
  const [currentVersionActions, setCurrentVersionActions] = useState<
    VersionActions[]
  >([]);

  // Стейт прогресса автокорректуры — драйвит модалку и кнопку. active=true
  // пока джоб не завершён/не показан результат, потом юзер закрывает рукой.
  const [autocorrectState, setAutocorrectState] = useState<{
    active: boolean;
    status: "starting" | "pending" | "running" | "done" | "failed";
    done: number;
    total: number;
    current: string | null;
    error: string | null;
  }>({ active: false, status: "done", done: 0, total: 0, current: null, error: null });
  // Модалка ввода промпта перед стартом автокорректуры. `prompt` лежит уже
  // в стейте выше (из «Основное → Промпт»), модалка просто даёт его
  // отредактировать прямо здесь же.
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [hideAll, setHideAll] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [pagesCover, setPagesCover] = useState(0);

  const [viewMode, setViewMode] = useState<ViewMode>("basic");
  const [isLoadingSpinner, setIsLoadingSpinner] = useState(false);
  const [expandedPhotos, setExpandedPhotos] = useState<Set<string>>(new Set());
  // Когда заходим во view «Фото» — раскрываем все карточки сразу. Можно
  // схлопывать индивидуально кликом по бэйджу, но дефолт = развёрнутые.
  useEffect(() => {
    if (viewMode !== "photos") return;
    if (!userData?.photos?.length) return;
    setExpandedPhotos((prev) => {
      const next = new Set(prev);
      for (const p of userData.photos) next.add(p.photoUrl);
      return next;
    });
  }, [viewMode, userData?.photos]);

  // При входе в фото-вью автоматически выбираем самую свежую версию
  // (versions[0] — список отсортирован desc по createdAt). Если версий
  // нет — остаёмся на Оригинале. Если юзер уже на какой-то версии — не
  // трогаем его выбор.
  useEffect(() => {
    if (viewMode !== "photos") return;
    if (currentVersion) return;
    if (versions.length === 0) return;
    handleLoadVersion(versions[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, versions.length]);
  const [showDiff, setShowDiff] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // ApprovalPopup state. null = закрыт.
  const [approvalPopup, setApprovalPopup] = useState<{
    mode: "full" | "cover-only";
    bookFile?: File;
  } | null>(null);
  // «Скинуть клиенту» и последняя отправка (с правками клиента, если есть).
  const [sendPopupOpen, setSendPopupOpen] = useState(false);
  const [lastProof, setLastProof] = useState<ProofWithComments | null>(null);
  // Текущий статус заказа — нужен чтобы блокировать «Утвердить» когда он
  // ещё не «Отредачено» (бэк всё равно отбивает 409, но UI лучше говорить
  // об этом сразу).
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const canApprove = orderStatus === "Отредачено";

  // Попап превью страниц одного вопроса. null = закрыт. url — object-URL
  // сгенерированного бэком PDF-среза (ответ + прикреплённые к нему фото).
  const [pairPreview, setPairPreview] = useState<{
    question: string;
    url: string | null;
    pages: number;
    loading: boolean;
    error: string | null;
  } | null>(null);

  const editorId = currentUser?.id ? String(currentUser.id) : "";
  const editorName = currentUser?.username || "Unknown Editor";

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "Вы уверены что хотите закрыть?";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const fetchProofs = async (userId: string | number) => {
    try {
      const { data } = await client.get("/proofs/admin", { params: { userId } });
      const open = (data?.proofs || []).find((p: ProofWithComments) => p.status !== "superseded");
      setLastProof(open || null);
      if (data?.orderStatus) setOrderStatus(data.orderStatus);
    } catch (error) {
      console.error("Error fetching proofs:", error);
    }
  };

  // «Отметить»: звёздочка у версии. Хранится в versions.data (PATCH мёржит поля).
  const toggleStar = async () => {
    if (!currentVersion) return;
    const cur = versions.find((v) => v.id === currentVersion);
    const next = !cur?.starred;
    try {
      await client.patch(`/versions/${currentVersion}`, { starred: next });
      setVersions((prev) => prev.map((v) => (v.id === currentVersion ? { ...v, starred: next } : v)));
    } catch (error) {
      console.error("Error starring version:", error);
      setToastMessage("Не удалось отметить версию");
      window.setTimeout(() => setToastMessage(null), 3000);
    }
  };
  const currentStarred = !!versions.find((v) => v.id === currentVersion)?.starred;
  const starButton = (cls: string) =>
    currentVersion ? (
      <button
        className={`ep-btn ${cls} ep-star-btn ${currentStarred ? "is-starred" : ""}`}
        onClick={toggleStar}
        title={currentStarred ? "Снять отметку" : "Отметить версию — первой в «Скинуть клиенту»"}
      >
        <Star className="ep-btn-icon" size={16} fill={currentStarred ? "currentColor" : "none"} />
        {currentStarred ? "Отмечена" : "Отметить"}
      </button>
    ) : null;
  const canSendToClient = orderStatus === "Заполнил" || orderStatus === "Отредачено";

  const fetchVersionsForUser = async (userId: string) => {
    try {
      const { data: versionsList } = await client.get("/versions", { params: { userId } });
      const mapped: Versions[] = versionsList.map((v: any) => ({
        ...v,
        createdAt: new Date(v.createdAt),
      }));
      setVersions(mapped);
    } catch (error) {
      console.error("Error fetching versions:", error);
    }
  };

  // Возвращает юзеру доступ к /forms (Questions/Cover/Photos/Chapter).
  // Бэк переключает contacts.status с "done" на "pending" и, если заказ был
  // в «Заполнил», откатывает его в «Пишет» (книга уехала из редактуры назад).
  // Заказ, уже ушедший дальше по пайплайну, не трогается — тогда
  // statusRolledBack=false и мы говорим об этом явно.
  const openAccess = async (userId: string) => {
    try {
      const result = await apiCall(`/admin/contacts/${userId}/reopen`, {
        method: "POST",
      });
      if (!result.ok) {
        console.error("Reopen access failed:", result.status);
        setToastMessage("Не удалось открыть доступ");
        setTimeout(() => setToastMessage(null), 3000);
        return;
      }
      const data = await result.json().catch(() => null);
      setIsOpen(true);
      if (data?.statusRolledBack) {
        setOrderStatus("Пишет");
        setToastMessage('Доступ открыт, статус возвращён на "Пишет"');
      } else {
        if (data?.orderStatus) setOrderStatus(data.orderStatus);
        setToastMessage(
          data?.orderStatus
            ? `Доступ открыт. Статус остался "${data.orderStatus}"`
            : "Доступ открыт"
        );
      }
      setTimeout(() => setToastMessage(null), 3500);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async (chosen: string) => {
      setIsLoading(true);
      try {
        const userResponse = await apiCall(`/admin/user/${chosen}`);
        if (!userResponse.ok) throw new Error(`HTTP error! status: ${userResponse.status}`);
        const user = await userResponse.json();

        const analResponse = await apiCall(`/admin/contacts/${user._id}`);
        const analData = await analResponse.json();
        // contacts.status — enum "pending" | "done". Доступ к /forms закрыт
        // только если status === "done"; в любом другом случае (включая
        // отсутствие contact'а) считаем что доступ открыт и не показываем
        // кнопку «Открыть доступ».
        setIsOpen(analData.status !== "done");

        // Тащим статус заказа для гейта «Утвердить». /orders/:username
        // отдаёт строку заказа (есть staffMiddleware, токен auto-injects).
        try {
          const orderRes = await client.get(`/orders/${user.username}`);
          setOrderStatus(orderRes.data?.status || null);
        } catch (err) {
          setOrderStatus(null);
        }

        await fetchVersionsForUser(user._id);
        fetchProofs(user._id);

        const [coverResponse, photosResponse, questionsResponse, answersResponse] =
          await Promise.all([
            apiCall(`/admin/cover/${user._id}`),
            apiCall(`/admin/photos/${user._id}`),
            apiCall(`/admin/questions/${user.questionTemplates[0].id}`),
            apiCall(`/admin/answers/${user._id}/${user.questionTemplates[0].id}`),
          ]);

        let coverData: any = {};
        if (coverResponse.ok) {
          coverData = await coverResponse.json();
        } else {
          coverData = {
            authorName: "",
            partnerName: "",
            bookName: "",
            coverUrl: "cb-1-red",
            showPageNumbers: true,
            showFooterTitle: true,
            showFooterAuthor: true,
          };
        }

        // "" в edited_author_name = автора убрали; null = правки нет.
        setHideCoverAuthor(coverData.editedAuthorName === "");

        let photosData: any[] = [];
        if (photosResponse.ok) {
          const photosArray = await photosResponse.json();
          const uniquePhotos = new Set();
          photosData = photosArray
            .filter((photo: any) => {
              // Postgres-\u0441\u0445\u0435\u043c\u0430 \u0438\u0441\u043f\u043e\u043b\u044c\u0437\u0443\u0435\u0442 url / position / showDate /
              // showDescription / createdAt; legacy Mongo \u2014 photoUrl / status /
              // hideDate / hideDescription / date. \u041d\u043e\u0440\u043c\u0430\u043b\u0438\u0437\u0443\u0435\u043c \u043a admin-shape.
              const rawUrl = photo.url ?? photo.photoUrl ?? "";
              let questionTxt = photo.questionTxt || "";
              if ((questionTxt.startsWith('"') && questionTxt.endsWith('"')) ||
                  (questionTxt.startsWith('\u201c') && questionTxt.endsWith('\u201d'))) {
                questionTxt = questionTxt.slice(1, -1);
              }
              photo.questionToPrecede = questionTxt;
              photo.photoUrl = `${rawUrl}?size=thumbnail`;
              if (photo.status === undefined && photo.position !== undefined) {
                photo.status = photo.position;
              }
              if (photo.hideDate === undefined && photo.showDate !== undefined) {
                photo.hideDate = !photo.showDate;
              }
              if (photo.hideDescription === undefined && photo.showDescription !== undefined) {
                photo.hideDescription = !photo.showDescription;
              }
              // На created_at не подменяем: это момент загрузки файла, а не
              // дата снимка. Юзер даты не ставил — значит её и нет (пустая
              // строка после formatDate ниже).
              if (photo.isDeleted === undefined) photo.isDeleted = false;
              const sig = `${photo.photoUrl}-`;
              if (!uniquePhotos.has(sig)) {
                uniquePhotos.add(sig);
                return true;
              }
              return false;
            })
            .map((photo: any) => ({ ...photo, description: removeEmojis(photo.description), date: formatDate(photo.date) }));
        }

        let questionAnswerPairs: any[] = [];
        const original: Answers = {};

        if (user.questionTemplates.length > 0 && questionsResponse.ok && answersResponse.ok) {
          const questions = await questionsResponse.json();
          const answers = await answersResponse.json();

          if (Array.isArray(questions)) {
            questionAnswerPairs = questions
              .map((q, index) => {
                // Postgres-схема: questions.id / questions.text / answers.text.
                // Legacy Mongo называла их _id / question / answer — оставляем
                // фолбэки, чтобы старые роуты тоже не сломались.
                const qId = q.id ?? q._id;
                const qText = q.text ?? q.question ?? "";
                const answer = answers.find((a: any) => a.questionId === qId);
                const answerText = answer?.text ?? answer?.answer;
                return {
                  question: qText,
                  answer: answerText ? removeEmojis(answerText) : "No answer",
                  clientQuestion: answer?.clientQuestion || "",
                  chapter: getChapter(user.questionTemplates[0].id, index + 1),
                  number: index + 1,
                  isQuestionHidden: answer?.clientQuestion === "-",
                };
              })
              .filter(
                (pair) =>
                  pair.answer &&
                  !["-", " ", ".", ",", "No answer"].includes(pair.answer)
              );

            questionAnswerPairs.forEach((pair) => {
              original[pair.question] = pair.answer;
            });
            setOriginalAnswers(original);
            setCorrectedAnswers(original);
            setBaseAnswers(original);

            // Префилл «Кол-во страниц» — берём с бэка, который проходит
            // тот же layout, что Dashboard.tsx показывает юзеру:
            // front-matter + chapters + Σ answers.pageCount + photos.
            try {
              const pcRes = await client.get(
                `/admin/page-count/${user._id}/${user.questionTemplates[0].id}`
              );
              const total = Number(pcRes.data?.pageCount);
              if (Number.isFinite(total) && total > 0) {
                setPagesCover(total);
              }
            } catch (err) {
              console.error("page-count fetch failed", err);
            }
          }
        }

        // Store original photos and questions for comparison
        setOriginalPhotos(JSON.parse(JSON.stringify(photosData)));
        setOriginalQuestionPairs(JSON.parse(JSON.stringify(questionAnswerPairs)));

        // Предисловие: слева дефолт языка шаблона (его же печатает
        // генератор), справа правка. Оба поля приходят из /admin/cover.
        setUserData({
          ...user,
          ...coverData,
          photos: photosData,
          questionAnswerPairs,
          blanks: [],
          predislovie: coverData.defaultPreface || "",
        });
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (chosen) fetchData(chosen);
  }, [chosen]);

  const handleCorrectionChange = (question: string, correctedText: string) => {
    setCorrectedAnswers((prev) => ({ ...prev, [question]: correctedText }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (userData) setUserData({ ...userData, [name]: value });
  };

  // Переключатели футера: нумерация (showPageNumbers), название книги
  // (showFooterTitle) и имя автора (showFooterAuthor) — независимы.
  // Сразу сохраняем в covers — отдельной кнопки «сохранить» у «Основное» нет.
  const handleToggleFooter = async (
    field: "showPageNumbers" | "showFooterTitle" | "showFooterAuthor",
    checked: boolean,
  ) => {
    if (!userData) return;
    setUserData({ ...userData, [field]: checked });
    try {
      await client.patch(`/admin/cover/${userData._id}`, { [field]: checked });
    } catch (err) {
      console.error("[editor] toggle footer failed", err);
      setToastMessage("Не удалось сохранить настройки футера");
      setTimeout(() => setToastMessage(null), 3000);
      // Откатываем чекбокс, раз сохранить не удалось.
      setUserData((prev) => (prev ? { ...prev, [field]: !checked } : prev));
    }
  };

  // Правки редактуры (название/автор/получатель) — сохраняются в covers.edited_*
  // и имеют приоритет над оригиналом юзера при генерации книги/обложки.
  // Пустое поле = правки нет, используется оригинал.
  const [isSavingBookEdits, setIsSavingBookEdits] = useState(false);
  // Автор на обложке: пустое поле само по себе неоднозначно — оно выглядит
  // одинаково и когда правки не было, и когда автора убрали. Поэтому
  // «убрать» — отдельный тумблер: он пишет в covers.edited_author_name
  // пустую строку, тогда как «правки нет» это NULL (миграция 0032).
  const [hideCoverAuthor, setHideCoverAuthor] = useState(false);
  const saveBookEdits = async () => {
    if (!userData) return;
    setIsSavingBookEdits(true);
    try {
      const authorEdit = (userData.editedAuthorName ?? "").trim();
      await client.patch(`/admin/cover/${userData._id}`, {
        editedBookName: userData.editedBookName ?? "",
        // Тумблер важнее поля: включён — на обложке пусто. Иначе пустое поле
        // значит «правки нет» (null), и печатается имя, которое ввёл клиент.
        editedAuthorName: hideCoverAuthor ? "" : authorEdit || null,
        editedPartnerName: userData.editedPartnerName ?? "",
        editedPreface: userData.editedPreface ?? "",
      });
      setToastMessage("Правки книги сохранены");
    } catch (err) {
      console.error("[editor] save book edits failed", err);
      setToastMessage("Не удалось сохранить правки книги");
    } finally {
      setIsSavingBookEdits(false);
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  // Убрать правку предисловия: пустая строка в covers.edited_preface = книга
  // печатается с шаблонным текстом. Сохраняем сразу, чтобы «вернуть» значило
  // «вернулось», а не «не забудь ещё нажать Сохранить».
  const resetPreface = async () => {
    if (!userData) return;
    setIsSavingBookEdits(true);
    try {
      await client.patch(`/admin/cover/${userData._id}`, { editedPreface: "" });
      setUserData({ ...userData, editedPreface: "" });
      setToastMessage("Вернули шаблонное предисловие");
    } catch (err) {
      console.error("[editor] reset preface failed", err);
      setToastMessage("Не удалось вернуть шаблонное предисловие");
    } finally {
      setIsSavingBookEdits(false);
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const handlePhotoUpdate = (photoUrl: string, updates: Partial<Photo>) => {
    if (userData) {
      const updatedPhotos = userData.photos.map((photo) =>
        photo.photoUrl === photoUrl ? { ...photo, ...updates } : photo
      );
      setUserData({ ...userData, photos: updatedPhotos });
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addPhotoForQuestion, setAddPhotoForQuestion] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const handleAddPhotoClick = (question: string) => {
    setAddPhotoForQuestion(question);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userData || !addPhotoForQuestion) return;

    const questionForPhoto = addPhotoForQuestion;
    setAddPhotoForQuestion(null);
    if (fileInputRef.current) fileInputRef.current.value = "";

    setIsUploadingPhoto(true);
    try {
      // HEIC → JPEG до аплоада, иначе превью в админке не покажет (Safari
      // покажет, Chrome — нет) и фронтовый рендер книги тоже сломается.
      let uploadBlob: Blob = file;
      let uploadName = file.name;
      const isHeic =
        file.type === "image/heic" ||
        file.type === "image/heif" ||
        /\.(heic|heif)$/i.test(file.name);
      if (isHeic) {
        const heic2any = (await import("heic2any")).default;
        try {
          const converted = await heic2any({ blob: file, toType: "image/jpeg" });
          uploadBlob = Array.isArray(converted) ? converted[0] : converted;
          uploadName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
        } catch {
          // Если heic2any упал (например ERR_LIBHEIF format not supported) —
          // оставляем оригинал, серверный convertHeic подхватит при чтении.
        }
      }

      const formData = new FormData();
      formData.append("file", new File([uploadBlob], uploadName, { type: uploadBlob.type || "application/octet-stream" }));

      const response = await apiUpload("/upload", formData);
      if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
      const data = await response.json();
      const uploadedUrl: string | undefined = data?.url;
      if (!uploadedUrl) throw new Error("No URL returned from upload");

      const newPhoto: Photo = {
        imgData: null,
        photoUrl: uploadedUrl,
        date: new Date().toLocaleDateString("ru-RU"),
        description: "",
        questionToPrecede: questionForPhoto,
        hideDate: false,
        hideDescription: false,
        questionTxt: questionForPhoto,
        status: "center",
        predislovie: "",
        isDeleted: false,
        isNew: true,
      };

      setUserData((prev) =>
        prev ? { ...prev, photos: [...prev.photos, newPhoto] } : prev,
      );
    } catch (err) {
      console.error("Photo upload error:", err);
      setToastMessage("Не удалось загрузить фото");
      setTimeout(() => setToastMessage(""), 3000);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const saveCorrections = async () => {
    setIsLoadingSpinner(true);
    try {
      // Calculate photo and question changes
      const photoChanges = userData ? getChangedPhotos(userData.photos, originalPhotos) : [];
      const questionChanges = userData ? getChangedQuestions(userData.questionAnswerPairs, originalQuestionPairs) : [];

      // versions.data.answers хранит только дельту от answers-таблицы
      // (originalAnswers — снимок answers на момент загрузки редактора).
      // PDF-роут лукапит `answerMap[p.question] ?? p.answer` и сам падает
      // на базовый ответ юзера, если ключа в дельте нет (см. pdf.ts).
      const answersDelta = getChangedAnswers(correctedAnswers, originalAnswers);

      let resolvedVersionId = currentVersion;

      if (!currentVersion) {
        const { data: newVersion } = await client.post("/versions", {
          editor: editorName,
          answers: answersDelta,
          createdAt: new Date().toISOString(),
          actions: [{ action: `Created new version by ${editorName}`, createdAt: new Date().toISOString() }],
          userId: userData?._id,
          ...(photoChanges.length > 0 && { photoChanges }),
          ...(questionChanges.length > 0 && { questionChanges }),
        });
        resolvedVersionId = newVersion.id;

        // Optimistic update: вставляем свежую версию в локальный список
        // СРАЗУ, не дожидаясь refetch'а ниже. Без этого новая версия
        // появляется в сайдбаре только после второго `await` (GET /versions),
        // а до тех пор `versions` остаётся со старым набором.
        const newVersionMapped: Versions = {
          ...newVersion,
          createdAt: new Date(newVersion.createdAt),
        };
        setVersions((prev) => [newVersionMapped, ...prev]);
        setCurrentVersion(resolvedVersionId);

        await client.post("/notifications", {
          userId: editorId,
          message: `${editorName} создал новую версию для ${userData?.username}.`,
          timestamp: new Date().toISOString(),
        });
      } else {
        // PATCH мерджится на сервере shallow'ом, так что прежний editor/
        // createdAt/etc сохраняются автоматически — клиенту достаточно
        // прислать дельту и обновлённый actions-лог.
        const updatedActions = [
          ...currentVersionActions,
          { action: `Updated by ${editorName}`, createdAt: new Date().toISOString() },
        ];
        await client.patch(`/versions/${currentVersion}`, {
          answers: answersDelta,
          actions: updatedActions,
          photoChanges,
          questionChanges,
        });
      }

      setBaseAnswers(correctedAnswers);
      // Синхронизируем pair.answer в userData.questionAnswerPairs с тем,
      // что только что сохранилось как version.answers.
      if (userData) {
        setUserData({
          ...userData,
          questionAnswerPairs: userData.questionAnswerPairs.map((pair) => ({
            ...pair,
            answer:
              correctedAnswers[pair.question] !== undefined
                ? correctedAnswers[pair.question]
                : pair.answer,
          })),
        });
      }
      // ВАЖНО: originalPhotos / originalQuestionPairs — это снимок
      // order-уровня. Их нельзя перезаписывать current'ом userData.* при
      // сохранении версии, иначе все последующие applyDraftToPhotos /
      // applyQuestionChanges будут считать дельту от уже искажённой базы,
      // и переключение на другую версию ломается до релоуда.

      // Перетягиваем список версий с сервера, чтобы новая запись пришла
      // со всеми флэшнутыми server-side полями.
      let freshVersions: Versions[] | null = null;
      if (userData?._id) {
        try {
          const { data: versionsList } = await client.get("/versions", {
            params: { userId: userData._id },
          });
          freshVersions = versionsList.map((v: any) => ({
            ...v,
            createdAt: new Date(v.createdAt),
          }));
          setVersions(freshVersions!);
        } catch (err) {
          console.error("Error fetching versions:", err);
        }
      }

      // После refetch — синхронизируем actions-лог текущей версии. Без
      // этого «Updated by …» / «Created…» появляется в UI только после
      // релоуда страницы, потому что setCurrentVersionActions нигде
      // в saveCorrections раньше не звался.
      if (freshVersions && resolvedVersionId != null) {
        const cur = freshVersions.find(
          (v) => String(v.id) === String(resolvedVersionId)
        );
        setCurrentVersionActions(cur?.actions ?? []);
      }
    } catch (error) {
      console.error("Error saving version:", error);
    }
    setIsLoadingSpinner(false);
  };

  const handleLoadOriginalAnswers = () => {
    setCurrentVersion(null);
    setCurrentVersionActions([]);
    setCorrectedAnswers(originalAnswers);
    setBaseAnswers(originalAnswers);
    if (userData) {
      setUserData({
        ...userData,
        photos: JSON.parse(JSON.stringify(originalPhotos)),
        questionAnswerPairs: JSON.parse(JSON.stringify(originalQuestionPairs)).map((pair: QuestionAnswerPair) => ({
          ...pair,
          answer: originalAnswers[pair.question],
        })),
      });
    }
  };

  const handleLoadVersion = async (versionId: string | number) => {
    try {
      const { data: versionDoc } = await client.get(`/versions/${versionId}`);

      if (versionDoc) {
        // versionDoc.answers — дельта от answers-таблицы. В редакторе
        // показываем «эффективный» ответ = базовый ⊕ дельта, иначе
        // неизменённые вопросы выглядели бы пустыми.
        const effective = { ...originalAnswers, ...(versionDoc.answers || {}) };
        setCorrectedAnswers(effective);
        setBaseAnswers(effective);
        setCurrentVersion(versionId);
        setCurrentVersionActions(versionDoc.actions || []);

        // Apply photo changes if they exist in this version
        const versionPhotoChanges = versionDoc.photoChanges || [];
        const updatedPhotos = versionPhotoChanges.length > 0
          ? applyDraftToPhotos(JSON.parse(JSON.stringify(originalPhotos)), versionPhotoChanges)
          : JSON.parse(JSON.stringify(originalPhotos));

        // Apply question changes if they exist in this version
        const versionQuestionChanges = versionDoc.questionChanges || [];
        let updatedPairs = JSON.parse(JSON.stringify(originalQuestionPairs)) as QuestionAnswerPair[];
        if (versionQuestionChanges.length > 0) {
          updatedPairs = applyQuestionChanges(updatedPairs, versionQuestionChanges);
        }

        if (userData) {
          setUserData({
            ...userData,
            photos: updatedPhotos,
            questionAnswerPairs: updatedPairs.map((pair) => ({
              ...pair,
              answer: effective[pair.question] ?? pair.answer,
            })),
          });
        }
      }
    } catch (error) {
      console.error("Error loading version:", error);
    }
  };

  // Автокоррект через бэк-роут /api/admin/autocorrect — асинхронный джоб
  // с поллингом прогресса. По окончании новая версия с editor="Автокоррект"
  // создаётся на сервере и подтягивается через handleLoadVersion.
  const autocorrect = async () => {
    if (!userData) return;
    setAutocorrectState({ active: true, status: "starting", done: 0, total: 0, current: null, error: null });
    try {
      const { data: start } = await client.post<{ jobId: string }>(
        "/admin/autocorrect/start",
        {
          userId: userData._id,
          source: currentVersion ?? "original",
          prompt,
        },
      );
      const jobId = start.jobId;
      // Поллим каждые 1.5 c. Прерываем только когда статус терминальный.
      while (true) {
        await new Promise((r) => setTimeout(r, 1500));
        const { data: state } = await client.get<{
          status: "pending" | "running" | "done" | "failed";
          total: number;
          done: number;
          current: string | null;
          error: string | null;
          newVersionId: number | null;
        }>(`/admin/autocorrect/${jobId}`);
        setAutocorrectState({
          active: true,
          status: state.status,
          done: state.done,
          total: state.total,
          current: state.current,
          error: state.error,
        });
        if (state.status === "done") {
          await fetchVersionsForUser(userData._id);
          if (state.newVersionId != null) {
            await handleLoadVersion(state.newVersionId);
          }
          break;
        }
        if (state.status === "failed") {
          throw new Error(state.error || "Job failed");
        }
      }
      setAutocorrectState((s) => ({ ...s, active: false, status: "done" }));
    } catch (error: any) {
      console.error("Autocorrect error:", error);
      setAutocorrectState({
        active: true,
        status: "failed",
        done: 0,
        total: 0,
        current: null,
        error: error?.response?.data?.error || error?.message || "Ошибка",
      });
    }
  };

  // «Утвердить» с пустыми слотами — редактор сам грузит обложку и книгу.
  const openApprovalEmpty = () => {
    setApprovalPopup({ mode: "full" });
  };

  // format: undefined = печатный формат (цех Алматы), "5mm" = тест 128×188 мм.
  const handleGeneratePDF = async (format?: "5mm") => {
    if (!userData) return;
    setIsLoading(true);
    try {
      const userId = userData._id;
      const templateId = userData.questionTemplates[0].id;
      // Если редактор открыл версию — рендерим именно её. Несохранённые
      // правки в локальном state в PDF не попадут, нужно сначала «Сохранить».
      const params = new URLSearchParams();
      if (currentVersion) params.set("versionId", String(currentVersion));
      if (format) params.set("format", format);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const url = `/pdf/admin/${userId}/${templateId}${qs}`;
      const response = await client.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const suffix = currentVersion ? `v${currentVersion}` : "base";
      const fmtSuffix = format ? `_${format}` : "";
      const author = userData.editedAuthorName || userData.authorName || `user${userId}`;
      saveAs(blob, `${author}_${suffix}${fmtSuffix}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
    setIsLoading(false);
  };

  // ── Превью страниц одного вопроса ──────────────────────────────────────
  // Бэк рендерит срез книги (страницы ответа + страницы прикреплённых фото)
  // тем же генератором, что и полную книгу. Текущее состояние редактора
  // уходит снэпшотом — того же формата, что versions.data, — поэтому в
  // превью видно и несохранённые правки.
  const closePairPreview = () => {
    setPairPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  };

  // Object-URL живёт до закрытия попапа; на размонтировании подчищаем.
  useEffect(() => {
    return () => {
      if (pairPreview?.url) URL.revokeObjectURL(pairPreview.url);
    };
  }, [pairPreview?.url]);

  const openPairPreview = async (question: string) => {
    if (!userData) return;
    const templateId = userData.questionTemplates[0]?.id;
    if (!templateId) return;

    // Открытый ранее PDF больше не нужен.
    if (pairPreview?.url) URL.revokeObjectURL(pairPreview.url);
    setPairPreview({ question, url: null, pages: 0, loading: true, error: null });

    try {
      const snapshot = {
        answers: getChangedAnswers(correctedAnswers, originalAnswers),
        photoChanges: getChangedPhotos(userData.photos, originalPhotos),
        questionChanges: getChangedQuestions(
          userData.questionAnswerPairs,
          originalQuestionPairs
        ),
      };
      const { data } = await client.post(
        `/pdf/admin/${userData._id}/${templateId}/pair`,
        { question, snapshot },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setPairPreview((prev) =>
        prev?.question === question ? { ...prev, url, loading: false } : prev
      );
    } catch (err: any) {
      // responseType: "blob" делает Blob'ом и тело ошибки — достаём текст.
      let message = "Не удалось собрать превью";
      try {
        const body = err?.response?.data;
        const text = body instanceof Blob ? await body.text() : "";
        if (text) message = JSON.parse(text).error || message;
      } catch {
        /* тело не JSON — оставляем дефолтный текст */
      }
      console.error("Pair preview error:", err);
      setPairPreview((prev) =>
        prev?.question === question ? { ...prev, loading: false, error: message } : prev
      );
    }
  };

  // Cover generator. Backend route собирает PDF: solid color (средний цвет
  // передней обложки) + корешок с логотипом + сама передняя обложка с
  // оверлеем title/author по выбранному пресету
  // (frame|frame-top|left|center). Пресет юзера лежит в covers.plate_preset,
  // но здесь админ выбирает руками — ?preset= перебивает значение из БД.
  const userCoverPreset = asCoverPreset(userData?.platePreset);
  // Обложки Comabooks (cb-*): вёрстка задана пресетом, plate_preset не нужен.
  const cbCover = comabooksCoverById(userData?.coverUrl);
  const [cbPickerOpen, setCbPickerOpen] = useState(false);

  const handleChangeComabooksCover = async (id: string) => {
    if (!userData || id === userData.coverUrl) return;
    const next = comabooksCoverById(id);
    if (!next || !window.confirm(`Сменить обложку клиента на «${next.name}»?`)) return;
    try {
      await client.patch(`/admin/cover/${userData._id}`, { coverUrl: id });
      setUserData((prev) => (prev ? { ...prev, coverUrl: id } : prev));
      setCbPickerOpen(false);
    } catch (err) {
      console.error("Error changing cover:", err);
      alert("Не удалось сменить обложку.");
    }
  };

  const handleGenerateCover = async (preset: CoverPreset) => {
    if (!userData || pagesCover <= 0) return;
    setIsLoading(true);
    try {
      const userId = userData._id;
      const templateId = userData.questionTemplates[0].id;
      const response = await client.get(
        `/pdf/admin/${userId}/${templateId}/cover`,
        { params: { pageCount: pagesCover, preset }, responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const author = userData.editedAuthorName || userData.authorName || `user${userId}`;
      saveAs(blob, `${author}_обложка_${preset}.pdf`);
    } catch (error) {
      console.error("Error generating cover:", error);
      alert("Не удалось сгенерировать обложку. См. консоль.");
    }
    setIsLoading(false);
  };

  const toggleQuestionVisibility = (question: string) => {
    setUserData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        questionAnswerPairs: prev.questionAnswerPairs.map((pair) =>
          pair.question === question ? { ...pair, isQuestionHidden: !pair.isQuestionHidden } : pair
        ),
      };
    });
  };

  const removeQuestionAnswerPair = (question: string) => {
    setUserData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        questionAnswerPairs: prev.questionAnswerPairs.map((pair) =>
          pair.question === question ? { ...pair, isRemoved: !pair.isRemoved } : pair
        ),
      };
    });
  };

  const updateClientQuestion = (question: string, newClientQuestion: string) => {
    setUserData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        questionAnswerPairs: prev.questionAnswerPairs.map((pair) =>
          pair.question === question ? { ...pair, clientQuestion: newClientQuestion } : pair
        ),
      };
    });
  };

  const toggleHideAllQuestions = () => {
    setUserData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        questionAnswerPairs: prev.questionAnswerPairs.map((pair) => ({
          ...pair,
          isQuestionHidden: !hideAll,
        })),
      };
    });
    setHideAll(!hideAll);
  };

  const close = () => {
    setUserData(null);
    setCorrectedAnswers({});
    setCurrentVersion(null);
    setOriginalAnswers({});
    setVersions([]);
    onClose();
  };

  const getPhotosForQuestion = (question: string) => {
    const result = userData?.photos.filter((p) => p.questionToPrecede === question) || [];
    return result;
  };

  // Debug: log once when userData changes
  useEffect(() => {
    if (userData?.photos.length && userData?.questionAnswerPairs.length) {
      console.log('=== DEBUG: Questions vs Photos ===');
      console.log('Questions in list:', userData.questionAnswerPairs.map(p => p.question));
      console.log('Photos questionToPrecede:', [...new Set(userData.photos.map(p => p.questionToPrecede))]);
    }
  }, [userData]);

  const togglePhoto = (photoUrl: string) => {
    setExpandedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(photoUrl)) next.delete(photoUrl);
      else next.add(photoUrl);
      return next;
    });
  };

  const handleSwitchDiff = () => {
    setShowDiff((prev) => !prev);
  };

  // Реальные пресеты фото-страницы (совпадают с PHOTO_PRESETS бэкенда —
  // см. backend/src/services/pdfGenerator.ts и
  // frontend/src/components/PhotoPage/photoPresets.ts).
  // photos.position хранит сложный формат
  //     <preset>::<datePos>:<dateSize>::<descPos>:<descSize>::<color>
  // — при смене preset'а в админке надо подменить только первый сегмент,
  // оставив слоты/цвет как было, иначе на PDF сбрасывается лэйаут юзера.
  const PHOTO_PRESET_KEYS = [
    "fullbleed",
    "square",
    "landscape",
    "landscape-wide",
    "portrait",
    "portrait-tall",
  ] as const;
  type PhotoPresetKey = (typeof PHOTO_PRESET_KEYS)[number];
  const PHOTO_PRESET_LABELS: Record<PhotoPresetKey, string> = {
    fullbleed: "на весь фон",
    square: "квадрат",
    landscape: "ландшафт 4:3",
    "landscape-wide": "панорама 16:9",
    portrait: "портрет 3:4",
    "portrait-tall": "портрет 9:16",
  };
  const DEFAULT_PHOTO_POS = {
    preset: "fullbleed" as PhotoPresetKey,
    datePos: "top",
    dateSize: "sm",
    descPos: "bottom",
    descSize: "md",
    color: "light",
  };
  function parsePhotoPosition(raw: string | null | undefined) {
    if (!raw) return { ...DEFAULT_PHOTO_POS };
    const parts = raw.split("::");
    const preset = (PHOTO_PRESET_KEYS as readonly string[]).includes(parts[0])
      ? (parts[0] as PhotoPresetKey)
      : DEFAULT_PHOTO_POS.preset;
    const [datePos = DEFAULT_PHOTO_POS.datePos, dateSize = DEFAULT_PHOTO_POS.dateSize] =
      (parts[1] ?? "").split(":");
    const [descPos = DEFAULT_PHOTO_POS.descPos, descSize = DEFAULT_PHOTO_POS.descSize] =
      (parts[2] ?? "").split(":");
    const color = parts[3] === "dark" ? "dark" : DEFAULT_PHOTO_POS.color;
    return { preset, datePos, dateSize, descPos, descSize, color };
  }
  function encodePhotoPosition(p: ReturnType<typeof parsePhotoPosition>): string {
    return `${p.preset}::${p.datePos}:${p.dateSize}::${p.descPos}:${p.descSize}::${p.color}`;
  }

  // Помощник: ряд кнопок-пикеров (расположение / размер / цвет).
  const photoBtnRow = (
    opts: readonly { key: string; label: string }[],
    current: string,
    onPick: (key: string) => void,
    disabled?: boolean
  ) => (
    <div className="ep-photo-positions">
      {opts.map((o) => (
        <button
          key={o.key}
          className={`ep-photo-pos-btn ${current === o.key ? "active" : ""}`}
          disabled={disabled}
          onClick={() => onPick(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  // Пикеры расположения + размера текста — кладутся прямо под инпут даты/описания
  // (без отдельных заголовков). Меняют сегменты того же photo.status.
  const renderPhotoTextControls = (photo: Photo, which: "date" | "desc") => {
    // Comabooks: позицию текста задаёт формат фото, отдельных слотов/размеров нет.
    if (COMABOOKS_BOOK_DESIGN) return null;
    const parsed = parsePhotoPosition(photo.status);
    const update = (patch: Partial<typeof parsed>) =>
      handlePhotoUpdate(photo.photoUrl, {
        status: encodePhotoPosition({ ...parsed, ...patch }),
      });
    const isDate = which === "date";
    const disabled = isDate ? photo.hideDate : photo.hideDescription;
    const pos = isDate ? parsed.datePos : parsed.descPos;
    const size = isDate ? parsed.dateSize : parsed.descSize;
    return (
      <div className="ep-photo-text-controls">
        {photoBtnRow(
          PHOTO_SLOT_OPTS,
          pos,
          (k) => update(isDate ? { datePos: k } : { descPos: k }),
          disabled
        )}
        {photoBtnRow(
          PHOTO_SIZE_OPTS,
          size,
          (k) => update(isDate ? { dateSize: k } : { descSize: k }),
          disabled
        )}
      </div>
    );
  };

  // Цвет текста — общий для даты и описания, остаётся отдельным полем.
  const renderPhotoColorControls = (photo: Photo) => {
    if (COMABOOKS_BOOK_DESIGN) return null;
    const parsed = parsePhotoPosition(photo.status);
    return (
      <div className="ep-photo-field">
        <span>Цвет текста</span>
        {photoBtnRow(PHOTO_COLOR_OPTS, parsed.color, (k) =>
          handlePhotoUpdate(photo.photoUrl, {
            status: encodePhotoPosition({ ...parsed, color: k }),
          })
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="ep-loading">
        <div className="ep-loading-content">
          <div className="ep-loading-spinner" />
          <div className="ep-loading-text">Загрузка...</div>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  return (
    <div className="ep-container">
      {toastMessage && (
        <div className="ep-toast">{toastMessage}</div>
      )}
      {isUploadingPhoto && (
        <div className="ep-toast">Загружаю фото…</div>
      )}
      {/* Hidden file input for adding photos */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleFileSelect}
      />

      {/* Sidebar */}
      <aside className="ep-sidebar">
        <div className="ep-sidebar-header">
          <button className="ep-sidebar-close" onClick={close}>
            <X size={20} />
          </button>
          <div className="ep-user-info">
            <div className="ep-username">{userData.username}</div>
            <div className="ep-stats">
              <div className="ep-stat">
                <MessageSquare className="ep-stat-icon" size={14} />
                {userData.questionAnswerPairs.length} вопросов
              </div>
              <div className="ep-stat">
                <Image className="ep-stat-icon" size={14} />
                {userData.photos.length} фото
              </div>
              <div className="ep-stat">
                <FileText className="ep-stat-icon" size={14} />
                {questionTemplateToRecipientMap[userData.questionTemplates[0]?.id] || "\u2014"}
              </div>
            </div>

          </div>
        </div>

        <div className="ep-sidebar-content">
          {/* Navigation */}
          <div className="ep-sidebar-section">
            <button
              className={`ep-sidebar-btn ${viewMode === "basic" ? "active" : ""}`}
              onClick={() => setViewMode("basic")}
            >
              <Settings className="ep-sidebar-btn-icon" size={18} />
              Основное
            </button>
            <button
              className={`ep-sidebar-btn ${viewMode === "answers" ? "active" : ""}`}
              onClick={() => setViewMode("answers")}
            >
              <BookOpen className="ep-sidebar-btn-icon" size={18} />
              Ответы
            </button>
            <button
              className={`ep-sidebar-btn ${viewMode === "photos" ? "active" : ""}`}
              onClick={() => setViewMode("photos")}
            >
              <Image className="ep-sidebar-btn-icon" size={18} />
              Фото
            </button>
          </div>

          {/* Versions. Клик по версии автоматически переключает в режим
           * «Ответы», чтобы юзеру не приходилось перед этим жать вкладку
           * вручную. */}
          <div className="ep-sidebar-section">
            <div className="ep-section-title">Версии</div>
            <div className="ep-versions-list">
              {/* Original */}
              <div className="ep-version-group">
                <div className="ep-version-row">
                  <button
                    className={`ep-version-btn ${!currentVersion ? "active" : ""}`}
                    onClick={() => {
                      if (viewMode === "basic") setViewMode("answers");
                      handleLoadOriginalAnswers();
                    }}
                  >
                    <span className="ep-version-editor">
                      Оригинал
                      {lastProof?.status === "changes" && lastProof.versionId === null && (
                        <span className="ep-version-badge">правки</span>
                      )}
                    </span>
                    <span className="ep-version-date">Исходные ответы</span>
                  </button>
                </div>
              </div>

              {versions.map((version) => (
                <div key={version.id} className="ep-version-group">
                  <div className="ep-version-row">
                    <button
                      className={`ep-version-btn ${currentVersion === version.id ? "active" : ""}`}
                      onClick={() => {
                        if (viewMode === "basic") setViewMode("answers");
                        handleLoadVersion(version.id);
                      }}
                    >
                      <span className="ep-version-editor">
                        {version.starred && <Star size={13} className="ep-version-star" fill="currentColor" />}
                        {version.editor}
                        {lastProof?.status === "changes" && lastProof.versionId === Number(version.id) && (
                          <span className="ep-version-badge">правки</span>
                        )}
                      </span>
                      <span className="ep-version-date">{formatDate2(version.createdAt)}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cover */}
          <div className="ep-sidebar-section">
            <div className="ep-section-title">Обложка</div>
            <div className="ep-cover-group">
              <input
                type="number"
                className="ep-cover-input"
                value={pagesCover || ""}
                onChange={(e) => setPagesCover(Number(e.target.value))}
                placeholder="Кол-во страниц"
              />
              {cbCover ? (
                <>
                  <div className="ep-cb-cover">
                    <img src={cbCover.thumb} alt="" className="ep-cb-cover__thumb" />
                    <div className="ep-cb-cover__info">
                      <div className="ep-cb-cover__name">{cbCover.name}</div>
                      <button
                        type="button"
                        className="ep-cb-cover__change"
                        onClick={() => setCbPickerOpen((v) => !v)}
                      >
                        {cbPickerOpen ? "Скрыть" : "Сменить дизайн"}
                      </button>
                    </div>
                  </div>
                  {cbPickerOpen && (
                    <div className="ep-cb-grid">
                      {COMABOOKS_COVERS.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          title={c.name}
                          className={`ep-cb-grid__item ${c.id === cbCover.id ? "ep-cb-grid__item--active" : ""}`}
                          onClick={() => handleChangeComabooksCover(c.id)}
                        >
                          <img src={c.thumb} alt={c.name} />
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="ep-cover-presets">
                    <button
                      className="ep-btn ep-btn-primary"
                      onClick={() => handleGenerateCover(userCoverPreset)}
                      title="Печатная обложка Comabooks (формат Алматы)"
                    >
                      Сгенерировать обложку
                    </button>
                  </div>
                </>
              ) : (
                <>
              {/* Первой кнопкой — то, что выбрал сам юзер: в большинстве
                  случаев печатать надо именно так. Остальные пресеты ниже,
                  как ручной override. */}
              <div className="ep-cover-presets">
                {COVER_PRESETS.filter((p) => p.value === userCoverPreset).map((p) => (
                  <button
                    key={p.value}
                    className="ep-btn ep-btn-primary"
                    onClick={() => handleGenerateCover(p.value)}
                    title={p.hint}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="ep-cover-presets">
                {COVER_PRESETS.filter((p) => p.value !== userCoverPreset).map((p) => (
                  <button
                    key={p.value}
                    className="ep-btn ep-btn-secondary"
                    onClick={() => handleGenerateCover(p.value)}
                    title={p.hint}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
                </>
              )}
            </div>
          </div>

        </div>

        {/* Sidebar Footer */}
        <div className="ep-sidebar-footer">
          {viewMode === "answers" && (
            <>
              <div className="ep-pdf-btn-group">
                <button
                  className="ep-btn ep-btn-secondary"
                  onClick={() => handleGeneratePDF()}
                  disabled={isLoading}
                  title="В формате цеха заказа (цех — в карточке заказа)"
                >
                  <Download className="ep-btn-icon" size={16} />
                  PDF
                </button>
                <button
                  className="ep-btn ep-btn-secondary"
                  onClick={() => handleGeneratePDF("5mm")}
                  disabled={isLoading}
                  title="128×188 мм — 1085×1594 px (тот же масштаб, +2 мм по каждой стороне)"
                >
                  5mm
                </button>
              </div>
              <button
                className="ep-btn ep-btn-primary ep-save-version-btn"
                onClick={saveCorrections}
                disabled={isLoadingSpinner}
              >
                {isLoadingSpinner ? "Сохранение..." : currentVersion ? "Обновить версию" : "Создать версию"}
              </button>
            </>
          )}
          {canSendToClient && (
            <button className="ep-btn ep-btn-primary" onClick={() => setSendPopupOpen(true)}>
              <Send className="ep-btn-icon" size={16} />
              Скинуть клиенту
            </button>
          )}
          {canApprove && (
            <button
              className="ep-btn ep-btn-primary"
              onClick={openApprovalEmpty}
              title="Утвердить (пустые слоты — загрузить вручную)"
            >
              Утвердить
            </button>
          )}
          {!isOpen && (
            <button className="ep-btn ep-btn-secondary" onClick={() => openAccess(userData._id)}>
              <Unlock className="ep-btn-icon" size={16} />
              Открыть доступ
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="ep-main">
        <div className="ep-main-header">
          <div className="ep-main-title">
            {viewMode === "basic"
              ? "Основная информация"
              : viewMode === "photos"
              ? "Фотографии"
              : "Редактирование ответов"}
          </div>
          {viewMode !== "basic" && (
            <div className="ep-main-actions">
              {starButton("ep-btn-secondary")}
              <button
                className="ep-btn ep-btn-primary"
                onClick={saveCorrections}
                disabled={isLoadingSpinner}
              >
                {isLoadingSpinner
                  ? "Сохранение..."
                  : currentVersion
                  ? "Обновить версию"
                  : "Создать версию"}
              </button>
            </div>
          )}
        </div>

        <div className="ep-main-content">
          {viewMode === "basic" && (
            <>
              <div className="ep-panel-row">
                <div className="ep-panel">
                  <div className="ep-panel-title">Информация о книге</div>
                  <div className="ep-form-group">
                    <label className="ep-label">Название книги</label>
                    <input
                      type="text"
                      className="ep-input"
                      value={userData.bookName || ""}
                      readOnly
                      placeholder="Оригинал юзера"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label className="ep-label">Автор</label>
                    <input
                      type="text"
                      className="ep-input"
                      value={userData.authorName || ""}
                      readOnly
                      placeholder="Оригинал юзера"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label className="ep-label">Получатель</label>
                    <input
                      type="text"
                      className="ep-input"
                      value={userData.partnerName || ""}
                      readOnly
                      placeholder="Оригинал юзера"
                    />
                  </div>
                  <div className="ep-form-group">
                    <div className="ep-toggle">
                      <input
                        type="checkbox"
                        id="showPageNumbers"
                        checked={userData.showPageNumbers !== false}
                        onChange={(e) =>
                          handleToggleFooter("showPageNumbers", e.target.checked)
                        }
                      />
                      <label htmlFor="showPageNumbers" className="ep-toggle-label">
                        Отображать нумерацию страниц
                      </label>
                    </div>
                    <div className="ep-toggle">
                      <input
                        type="checkbox"
                        id="showFooterTitle"
                        checked={userData.showFooterTitle !== false}
                        onChange={(e) =>
                          handleToggleFooter("showFooterTitle", e.target.checked)
                        }
                      />
                      <label htmlFor="showFooterTitle" className="ep-toggle-label">
                        Отображать название книги
                      </label>
                    </div>
                    <div className="ep-toggle">
                      <input
                        type="checkbox"
                        id="showFooterAuthor"
                        checked={userData.showFooterAuthor !== false}
                        onChange={(e) =>
                          handleToggleFooter("showFooterAuthor", e.target.checked)
                        }
                      />
                      <label htmlFor="showFooterAuthor" className="ep-toggle-label">
                        Отображать имя автора
                      </label>
                    </div>
                  </div>
                </div>

                <div className="ep-panel">
                  <div className="ep-panel-title">Правки</div>
                  <div className="ep-form-group">
                    <label className="ep-label">Название книги</label>
                    <input
                      type="text"
                      name="editedBookName"
                      className="ep-input"
                      value={userData.editedBookName || ""}
                      onChange={handleInputChange}
                      placeholder={userData.bookName || "Как в оригинале"}
                    />
                  </div>
                  <div className="ep-form-group">
                    <label className="ep-label">Автор</label>
                    <input
                      type="text"
                      name="editedAuthorName"
                      className="ep-input"
                      value={hideCoverAuthor ? "" : userData.editedAuthorName || ""}
                      onChange={handleInputChange}
                      disabled={hideCoverAuthor}
                      placeholder={
                        hideCoverAuthor
                          ? "На обложке автора не будет"
                          : userData.authorName || "Как в оригинале"
                      }
                    />
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 6,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={hideCoverAuthor}
                        onChange={(e) => setHideCoverAuthor(e.target.checked)}
                      />
                      Не печатать автора
                    </label>
                  </div>
                  <div className="ep-form-group">
                    <label className="ep-label">Получатель</label>
                    <input
                      type="text"
                      name="editedPartnerName"
                      className="ep-input"
                      value={userData.editedPartnerName || ""}
                      onChange={handleInputChange}
                      placeholder={userData.partnerName || "Как в оригинале"}
                    />
                  </div>
                  <div className="ep-form-group">
                    
                    <button
                      className="ep-btn ep-btn-primary"
                      onClick={saveBookEdits}
                      disabled={isSavingBookEdits}
                    >
                      {isSavingBookEdits ? "Сохранение..." : "Сохранить правки"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="ep-panel">
                <div className="ep-panel-title">Промпт для автокоррекции</div>
                <textarea
                  className="ep-textarea"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Введите промпт"
                />
              </div>

              {/* Предисловие — та же пара «оригинал / правка», что у названия
                  и автора выше. Слева дефолт языка шаблона (read-only, ровно
                  то, что печатается), справа правка: пусто = печатается
                  дефолт. Абзацы разделяются пустой строкой, последний абзац
                  уходит в подпись издательства. */}
              <div className="ep-panel-row">
                <div className="ep-panel">
                  <div className="ep-panel-title">Предисловие</div>
                  <textarea
                    className="ep-textarea"
                    value={userData.predislovie || ""}
                    readOnly
                    placeholder="Шаблонное предисловие"
                    style={{ minHeight: 550 }}
                  />
                </div>

                <div className="ep-panel">
                  <div className="ep-panel-title">Правка предисловия</div>
                  <textarea
                    className="ep-textarea"
                    name="editedPreface"
                    value={userData.editedPreface || ""}
                    onChange={handleInputChange}
                    placeholder={userData.predislovie || "Как в шаблоне"}
                    style={{ minHeight: 550 }}
                  />
                  <div className="ep-form-group">
                    <button
                      className="ep-btn ep-btn-primary"
                      onClick={saveBookEdits}
                      disabled={isSavingBookEdits}
                    >
                      {isSavingBookEdits ? "Сохранение..." : "Сохранить правки"}
                    </button>
                    <button
                      className="ep-btn ep-btn-secondary"
                      onClick={resetPreface}
                      disabled={isSavingBookEdits || !userData.editedPreface}
                      title="Убрать правку и вернуть в книгу шаблонное предисловие"
                    >
                      <RotateCcw className="ep-btn-icon" size={16} />
                      Вернуть шаблонное
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {viewMode === "answers" && (
            <>
              {/* Правки клиента — на странице той версии, что ему скинули
                  (у оригинала и своих файлов версии нет — на «Оригинале»). */}
              {lastProof?.status === "changes" &&
                (lastProof.versionId ?? null) === (currentVersion ? Number(currentVersion) : null) && (
                  <ProofComments proof={lastProof} />
                )}
              <div className="ep-answers-toolbar">
                <div className="ep-version-title">
                  {currentVersion
                    ? `Версия: ${versions.find(v => v.id === currentVersion)?.editor || ""}`
                    : "Оригинальная версия"}
                </div>

                {currentVersionActions.length > 0 && (
                  <div className="ep-version-actions">
                    {currentVersionActions.map((action, index) => (
                      <div className="ep-version-action" key={index}>
                        <span className="ep-version-action-text">{action.action}</span>
                        <span className="ep-version-action-time">
                          {formatActionDate(action.createdAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="ep-toolbar-row">
                  <div className="ep-toggle">
                    <input
                      type="checkbox"
                      id="hideAllQuestions"
                      checked={hideAll}
                      onChange={toggleHideAllQuestions}
                    />
                    <label htmlFor="hideAllQuestions" className="ep-toggle-label">
                      Скрыть все вопросы
                    </label>
                  </div>
                  <div className="ep-toolbar-actions">
                    <button
                      className={`ep-btn ${showDiff ? 'ep-btn-primary' : 'ep-btn-secondary'}`}
                      onClick={handleSwitchDiff}
                      title="Показать изменения"
                    >
                      <GitCompare className="ep-btn-icon" size={16} />
                      {showDiff ? "Скрыть волшебную палочку" : "Показать волшебную палочку"}
                    </button>
                    <button
                      className="ep-btn ep-btn-secondary"
                      onClick={() => setShowPromptModal(true)}
                      disabled={autocorrectState.active}
                    >
                      <Sparkles className="ep-btn-icon" size={16} />
                      Автокоррект
                    </button>
                    <button className="ep-btn ep-btn-secondary" onClick={saveCorrections}>
                      {isLoadingSpinner ? "Сохранение..." : currentVersion ? "Обновить версию" : "Создать версию"}
                    </button>
                    {starButton("ep-btn-secondary")}
                    <div className="ep-pdf-btn-group">
                      <button
                        className="ep-btn ep-btn-primary"
                        onClick={() => handleGeneratePDF()}
                        title="В формате цеха заказа (цех — в карточке заказа)"
                      >
                        <Download className="ep-btn-icon" size={16} />
                        PDF
                      </button>
                      <button
                        className="ep-btn ep-btn-secondary"
                        onClick={() => handleGeneratePDF("5mm")}
                        title="128×188 мм — 1085×1594 px (тот же масштаб, +2 мм по каждой стороне)"
                      >
                        5mm
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ep-qa-list">
                {userData.questionAnswerPairs.map((pair) => {
                  const photosForQuestion = getPhotosForQuestion(pair.question);

                  return (
                    <div
                      key={pair.question}
                      className={`ep-qa-item ${pair.isRemoved ? "removed" : ""}`}
                    >
                      <div className="ep-qa-header">
                        {editingQuestion === pair.question ? (
                          <div className="ep-qa-question-edit">
                            <input
                              type="text"
                              className="ep-qa-question-input"
                              value={pair.clientQuestion && pair.clientQuestion !== "-" ? pair.clientQuestion : pair.question}
                              onChange={(e) => updateClientQuestion(pair.question, e.target.value)}
                              onBlur={() => setEditingQuestion(null)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") setEditingQuestion(null);
                                if (e.key === "Escape") {
                                  updateClientQuestion(pair.question, originalQuestionPairs.find(p => p.question === pair.question)?.clientQuestion || "");
                                  setEditingQuestion(null);
                                }
                              }}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <div
                            className={`ep-qa-question ${pair.isQuestionHidden ? "hidden" : ""}`}
                            onClick={() => toggleQuestionVisibility(pair.question)}
                          >
                            {pair.clientQuestion && pair.clientQuestion !== "-" && pair.clientQuestion !== pair.question ? (
                              <>
                                <div className="ep-qa-client">Изменён: {pair.clientQuestion}</div>
                                <span style={{ textDecoration: "line-through", opacity: 0.5 }}>{pair.question}</span>
                              </>
                            ) : (
                              pair.question
                            )}
                          </div>
                        )}
                        <div className="ep-qa-actions">
                          <button
                            className="ep-qa-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPairPreview(pair.question);
                            }}
                            title="Превью страниц этого вопроса (с несохранёнными правками)"
                          >
                            <BookOpen size={16} />
                          </button>
                          <button
                            className="ep-qa-action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingQuestion(editingQuestion === pair.question ? null : pair.question);
                            }}
                            title="Редактировать вопрос"
                          >
                            <PenLine size={16} />
                          </button>
                          <button
                            className="ep-qa-action-btn"
                            onClick={() => toggleQuestionVisibility(pair.question)}
                            title={pair.isQuestionHidden ? "Показать вопрос" : "Скрыть вопрос"}
                          >
                            {pair.isQuestionHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            className="ep-qa-action-btn"
                            onClick={() => removeQuestionAnswerPair(pair.question)}
                            title={pair.isRemoved ? "Восстановить" : "Удалить"}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="ep-qa-body">
                        <div className="ep-qa-original">
                          <div className="ep-qa-label">Оригинал</div>
                          <div className="ep-qa-text">{originalAnswers[pair.question]}</div>
                        </div>
                        <div className="ep-qa-corrected">
                          <div className="ep-qa-label-row">
                            <div className="ep-qa-label">Редактирование</div>
                          </div>
                          {showDiff ? (
                            <DiffView
                              className="ep-qa-textarea"
                              originalText={originalAnswers[pair.question] || ""}
                              currentText={correctedAnswers[pair.question] || ""}
                              onChange={(value) => handleCorrectionChange(pair.question, value)}
                            />
                          ) : (
                            <AutoResizeTextarea
                              className="ep-qa-textarea"
                              value={correctedAnswers[pair.question] || ""}
                              onChange={(value) => handleCorrectionChange(pair.question, value)}
                            />
                          )}
                        </div>
                      </div>

                      {/* Photos for this question */}
                      {photosForQuestion.length > 0 && (
                        <div className="ep-photos-section">
                          {photosForQuestion.map((photo, idx) => (
                            <div key={`${photo.photoUrl}-${idx}`} className={`ep-photo-wrapper ${photo.isDeleted ? "ep-photo-deleted" : ""}`}>
                              <div
                                className={`ep-photo-badge ${expandedPhotos.has(photo.photoUrl) ? "active" : ""} ${photo.isDeleted ? "deleted" : ""}`}
                                onClick={() => togglePhoto(photo.photoUrl)}
                              >
                                <img src={photo.photoUrl} alt="" />
                                {photo.isDeleted ? (
                                  <span className="ep-photo-deleted-label">Удалено</span>
                                ) : (
                                  photo.description && !photo.hideDescription && (
                                    <span>{photo.description.substring(0, 25)}{photo.description.length > 25 ? '...' : ''}</span>
                                  )
                                )}
                                {expandedPhotos.has(photo.photoUrl) ? (
                                  <ChevronUp size={12} />
                                ) : (
                                  <ChevronDown size={12} />
                                )}
                              </div>
                              <button
                                className={`ep-photo-delete-btn ${photo.isDeleted ? "restore" : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePhotoUpdate(photo.photoUrl, { isDeleted: !photo.isDeleted });
                                }}
                                title={photo.isDeleted ? "Восстановить фото" : "Удалить фото"}
                              >
                                {photo.isDeleted ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                              </button>

                              {expandedPhotos.has(photo.photoUrl) && (
                                <div className="ep-photo-expanded">
                                  {/* PDF Preview */}
                                  <PhotoPreview photo={photo} />

                                  <div className="ep-photo-fields">
                                    <div className="ep-photo-field">
                                      <div className="ep-photo-field-header">
                                        <span>Дата</span>
                                        <label className="ep-photo-hide">
                                          <input
                                            type="checkbox"
                                            checked={photo.hideDate}
                                            onChange={(e) =>
                                              handlePhotoUpdate(photo.photoUrl, { hideDate: e.target.checked })
                                            }
                                          />
                                          скрыть
                                        </label>
                                      </div>
                                      <input
                                        type="text"
                                        className="ep-photo-input"
                                        value={photo.date}
                                        onChange={(e) =>
                                          handlePhotoUpdate(photo.photoUrl, { date: e.target.value })
                                        }
                                        disabled={photo.hideDate}
                                      />
                                      {renderPhotoTextControls(photo, "date")}
                                    </div>

                                    <div className="ep-photo-field">
                                      <div className="ep-photo-field-header">
                                        <span>Описание</span>
                                        <label className="ep-photo-hide">
                                          <input
                                            type="checkbox"
                                            checked={photo.hideDescription}
                                            onChange={(e) =>
                                              handlePhotoUpdate(photo.photoUrl, { hideDescription: e.target.checked })
                                            }
                                          />
                                          скрыть
                                        </label>
                                      </div>
                                      <textarea
                                        className="ep-photo-input"
                                        value={photo.description}
                                        onChange={(e) =>
                                          handlePhotoUpdate(photo.photoUrl, { description: e.target.value })
                                        }
                                        disabled={photo.hideDescription}
                                        rows={3}
                                      />
                                      {renderPhotoTextControls(photo, "desc")}
                                    </div>

                                    <div className="ep-photo-field">
                                      <span>Вопрос</span>
                                      <select
                                        className="ep-photo-input"
                                        value={photo.questionToPrecede}
                                        onChange={(e) => {
                                          const newQuestion = e.target.value;
                                          const oldQuestion = photo.questionToPrecede;
                                          if (newQuestion !== oldQuestion) {
                                            handlePhotoUpdate(photo.photoUrl, { questionToPrecede: newQuestion });
                                            const shortDesc = photo.description
                                              ? photo.description.substring(0, 20) + (photo.description.length > 20 ? "..." : "")
                                              : "Фото";
                                            const shortQuestion = newQuestion.substring(0, 30) + (newQuestion.length > 30 ? "..." : "");
                                            setToastMessage(`${shortDesc} \u2192 ${shortQuestion}`);
                                            setTimeout(() => setToastMessage(null), 3000);
                                          }
                                        }}
                                      >
                                        {userData.questionAnswerPairs.map((qaPair) => (
                                          <option key={qaPair.question} value={qaPair.question}>
                                            {qaPair.question.substring(0, 60)}{qaPair.question.length > 60 ? "..." : ""}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {COMABOOKS_BOOK_DESIGN ? (
                                      <CbPhotoFormatPicker
                                        status={photo.status}
                                        onPick={(status) => handlePhotoUpdate(photo.photoUrl, { status })}
                                      />
                                    ) : (
                                      <div className="ep-photo-field">
                                      <span>Рамка</span>
                                      <div className="ep-photo-positions">
                                        {PHOTO_PRESET_KEYS.map((preset) => {
                                          const parsed = parsePhotoPosition(photo.status);
                                          const isActive = parsed.preset === preset;
                                          return (
                                            <button
                                              key={preset}
                                              className={`ep-photo-pos-btn ${isActive ? "active" : ""}`}
                                              onClick={() =>
                                                handlePhotoUpdate(photo.photoUrl, {
                                                  status: encodePhotoPosition({ ...parsed, preset }),
                                                })
                                              }
                                            >
                                              {PHOTO_PRESET_LABELS[preset]}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    )}

                                    {renderPhotoColorControls(photo)}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                          {/* Add photo button */}
                          <button
                            className="ep-photo-add-btn"
                            onClick={() => handleAddPhotoClick(pair.question)}
                            disabled={isUploadingPhoto}
                            title={isUploadingPhoto ? "Загрузка…" : "Добавить фото"}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      )}
                      {/* Add photo button when no photos exist */}
                      {photosForQuestion.length === 0 && (
                        <div className="ep-photos-section">
                          <button
                            className="ep-photo-add-btn"
                            onClick={() => handleAddPhotoClick(pair.question)}
                            disabled={isUploadingPhoto}
                            title={isUploadingPhoto ? "Загрузка…" : "Добавить фото"}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {viewMode === "photos" && userData && (
            <div className="ep-photos-only">
              <div className="ep-photos-only-grid">
                {userData.photos.map((photo, idx) => (
                  <div
                    key={`${photo.photoUrl}-${idx}`}
                    className={`ep-photo-wrapper ${photo.isDeleted ? "ep-photo-deleted" : ""}`}
                  >
                    <div
                      className={`ep-photo-badge ${expandedPhotos.has(photo.photoUrl) ? "active" : ""} ${photo.isDeleted ? "deleted" : ""}`}
                      onClick={() => togglePhoto(photo.photoUrl)}
                    >
                      <img src={photo.photoUrl} alt="" />
                      {photo.isDeleted ? (
                        <span className="ep-photo-deleted-label">Удалено</span>
                      ) : (
                        photo.description && !photo.hideDescription && (
                          <span>
                            {photo.description.substring(0, 25)}
                            {photo.description.length > 25 ? "..." : ""}
                          </span>
                        )
                      )}
                      {expandedPhotos.has(photo.photoUrl) ? (
                        <ChevronUp size={12} />
                      ) : (
                        <ChevronDown size={12} />
                      )}
                    </div>
                    <button
                      className={`ep-photo-delete-btn ${photo.isDeleted ? "restore" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePhotoUpdate(photo.photoUrl, { isDeleted: !photo.isDeleted });
                      }}
                      title={photo.isDeleted ? "Восстановить фото" : "Удалить фото"}
                    >
                      {photo.isDeleted ? <RotateCcw size={14} /> : <Trash2 size={14} />}
                    </button>

                    {expandedPhotos.has(photo.photoUrl) && (
                      <div className="ep-photo-expanded">
                        <PhotoPreview photo={photo} />

                        <div className="ep-photo-fields">
                          <div className="ep-photo-field">
                            <div className="ep-photo-field-header">
                              <span>Дата</span>
                              <label className="ep-photo-hide">
                                <input
                                  type="checkbox"
                                  checked={photo.hideDate}
                                  onChange={(e) =>
                                    handlePhotoUpdate(photo.photoUrl, { hideDate: e.target.checked })
                                  }
                                />
                                скрыть
                              </label>
                            </div>
                            <input
                              type="text"
                              className="ep-photo-input"
                              value={photo.date}
                              onChange={(e) =>
                                handlePhotoUpdate(photo.photoUrl, { date: e.target.value })
                              }
                              disabled={photo.hideDate}
                            />
                            {renderPhotoTextControls(photo, "date")}
                          </div>

                          <div className="ep-photo-field">
                            <div className="ep-photo-field-header">
                              <span>Описание</span>
                              <label className="ep-photo-hide">
                                <input
                                  type="checkbox"
                                  checked={photo.hideDescription}
                                  onChange={(e) =>
                                    handlePhotoUpdate(photo.photoUrl, { hideDescription: e.target.checked })
                                  }
                                />
                                скрыть
                              </label>
                            </div>
                            <textarea
                              className="ep-photo-input"
                              value={photo.description}
                              onChange={(e) =>
                                handlePhotoUpdate(photo.photoUrl, { description: e.target.value })
                              }
                              disabled={photo.hideDescription}
                              rows={3}
                            />
                            {renderPhotoTextControls(photo, "desc")}
                          </div>

                          <div className="ep-photo-field">
                            <span>Вопрос</span>
                            <select
                              className="ep-photo-input"
                              value={photo.questionToPrecede}
                              onChange={(e) => {
                                const newQuestion = e.target.value;
                                const oldQuestion = photo.questionToPrecede;
                                if (newQuestion !== oldQuestion) {
                                  handlePhotoUpdate(photo.photoUrl, { questionToPrecede: newQuestion });
                                  const shortDesc = photo.description
                                    ? photo.description.substring(0, 20) + (photo.description.length > 20 ? "..." : "")
                                    : "Фото";
                                  const shortQuestion = newQuestion.substring(0, 30) + (newQuestion.length > 30 ? "..." : "");
                                  setToastMessage(`${shortDesc} → ${shortQuestion}`);
                                  setTimeout(() => setToastMessage(null), 3000);
                                }
                              }}
                            >
                              {userData.questionAnswerPairs.map((qaPair) => (
                                <option key={qaPair.question} value={qaPair.question}>
                                  {qaPair.question.substring(0, 60)}{qaPair.question.length > 60 ? "..." : ""}
                                </option>
                              ))}
                            </select>
                          </div>

                          {COMABOOKS_BOOK_DESIGN ? (
                            <CbPhotoFormatPicker
                              status={photo.status}
                              onPick={(status) => handlePhotoUpdate(photo.photoUrl, { status })}
                            />
                          ) : (
                            <div className="ep-photo-field">
                            <span>Рамка</span>
                            <div className="ep-photo-positions">
                              {PHOTO_PRESET_KEYS.map((preset) => {
                                const parsed = parsePhotoPosition(photo.status);
                                const isActive = parsed.preset === preset;
                                return (
                                  <button
                                    key={preset}
                                    className={`ep-photo-pos-btn ${isActive ? "active" : ""}`}
                                    onClick={() =>
                                      handlePhotoUpdate(photo.photoUrl, {
                                        status: encodePhotoPosition({ ...parsed, preset }),
                                      })
                                    }
                                  >
                                    {PHOTO_PRESET_LABELS[preset]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          )}

                          {renderPhotoColorControls(photo)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </main>

      {sendPopupOpen && userData && (
        <SendToClientPopup
          userId={userData._id}
          username={userData.username}
          initialVersionId={currentVersion ? Number(currentVersion) : null}
          onClose={() => setSendPopupOpen(false)}
          onSent={(notify) => {
            fetchProofs(userData._id);
            setToastMessage(sentToast(notify));
            window.setTimeout(() => setToastMessage(null), 5000);
          }}
        />
      )}

      {/* Approval popup */}
      {approvalPopup && userData && (
        <ApprovalPopup
          userId={userData._id}
          username={userData.username}
          mode={approvalPopup.mode}
          initialBookFile={approvalPopup.bookFile}
          prefillPages={pagesCover || undefined}
          onClose={() => setApprovalPopup(null)}
          onSaved={() => {
            setApprovalPopup(null);
            setToastMessage("Книга утверждена");
            window.setTimeout(() => setToastMessage(null), 3000);
          }}
        />
      )}

      {/* Превью страниц вопроса — PDF-срез книги (ответ + его фото). */}
      {pairPreview && (
        <div className="ep-pp-backdrop" onClick={closePairPreview}>
          <div className="ep-pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ep-pp-head">
              <div className="ep-pp-title">
                <span className="ep-pp-eyebrow">Превью страниц</span>
                <span className="ep-pp-question">{pairPreview.question}</span>
              </div>
              <div className="ep-pp-head-right">
                {pairPreview.pages > 0 && (
                  <span className="ep-pp-count">{pairPreview.pages} стр.</span>
                )}
                <button
                  className="ep-pp-close"
                  onClick={closePairPreview}
                  aria-label="Закрыть"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="ep-pp-body">
              {pairPreview.loading && <div className="ep-pp-hint">Собираю превью…</div>}
              {pairPreview.error && <div className="ep-pp-error">{pairPreview.error}</div>}
              {pairPreview.url && (
                <Document
                  file={pairPreview.url}
                  loading={<div className="ep-pp-hint">Загрузка PDF…</div>}
                  error={<div className="ep-pp-error">Не удалось открыть PDF</div>}
                  onLoadSuccess={({ numPages }: { numPages: number }) =>
                    setPairPreview((prev) => (prev ? { ...prev, pages: numPages } : prev))
                  }
                >
                  <div className="ep-pp-pages">
                    {Array.from({ length: pairPreview.pages }, (_, i) => (
                      <div key={i} className="ep-pp-page">
                        <Page
                          pageNumber={i + 1}
                          width={340}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                        <span className="ep-pp-page-num">{i + 1}</span>
                      </div>
                    ))}
                  </div>
                </Document>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Autocorrect prompt modal — открывается по клику «Автокоррект» */}
      {showPromptModal && !autocorrectState.active && (
        <div className="ep-ac-backdrop" onClick={() => setShowPromptModal(false)}>
          <div className="ep-ac-modal ep-ac-modal--prompt" onClick={(e) => e.stopPropagation()}>
            <div className="ep-ac-title">Промпт автокорректуры</div>
            <textarea
              className="ep-ac-prompt-area"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={10}
            />
            <div className="ep-ac-actions">
              <button
                className="ep-btn ep-btn-secondary"
                onClick={() => setPrompt(DEFAULT_PROMPT)}
              >
                Сбросить
              </button>
              <div style={{ flex: 1 }} />
              <button className="ep-btn ep-btn-secondary" onClick={() => setShowPromptModal(false)}>
                Отмена
              </button>
              <button
                className="ep-btn ep-btn-primary"
                onClick={() => {
                  setShowPromptModal(false);
                  autocorrect();
                }}
                disabled={!prompt.trim()}
              >
                Запустить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Autocorrect progress modal */}
      {autocorrectState.active && (
        <div className="ep-ac-backdrop">
          <div className="ep-ac-modal">
            <div className="ep-ac-title">
              {autocorrectState.status === "failed"
                ? "Автокорректура не удалась"
                : autocorrectState.status === "done"
                ? "Готово"
                : "Автокорректура"}
            </div>
            {autocorrectState.status === "failed" ? (
              <div className="ep-ac-error">{autocorrectState.error}</div>
            ) : (
              <>
                <div className="ep-ac-progress-text">
                  {autocorrectState.total > 0
                    ? `${autocorrectState.done} / ${autocorrectState.total}`
                    : "запускаем…"}
                </div>
                <div className="ep-ac-bar">
                  <div
                    className="ep-ac-bar-fill"
                    style={{
                      width:
                        autocorrectState.total > 0
                          ? `${Math.round((autocorrectState.done / autocorrectState.total) * 100)}%`
                          : "0%",
                    }}
                  />
                </div>
                {autocorrectState.current && autocorrectState.status === "running" && (
                  <div className="ep-ac-current">{autocorrectState.current}</div>
                )}
              </>
            )}
            {(autocorrectState.status === "done" || autocorrectState.status === "failed") && (
              <button
                className="ep-btn ep-btn-primary ep-ac-close"
                onClick={() =>
                  setAutocorrectState({
                    active: false,
                    status: "done",
                    done: 0,
                    total: 0,
                    current: null,
                    error: null,
                  })
                }
              >
                Закрыть
              </button>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default EditorPanel;
